import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './OverflowMenu.css';

export interface OverflowMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface OverflowMenuProps {
  items: OverflowMenuItem[];
  /** Accessible name for the trigger — say what it acts on, not just "menu". */
  label: string;
  disabled?: boolean;
}

interface MenuPosition {
  top: number;
  left: number;
}

const MENU_WIDTH_REM = 13;
const GAP_PX = 4;
/** Modal.tsx's backdrop; a click on it (and only it) closes the dialog. */
const MODAL_OVERLAY_CLASS = 'modal-overlay';

/**
 * `⋮` trigger with a portalled dropdown. Portalled because every consumer so far
 * sits inside a scrolling modal body, which would clip an absolute child.
 */
const OverflowMenu: React.FC<OverflowMenuProps> = ({ items, label, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const enabledIndexes = items
    .map((item, index) => (item.disabled ? -1 : index))
    .filter((index) => index >= 0);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Measure after paint so the menu's real height decides flip-up vs drop-down.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const width = MENU_WIDTH_REM * parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
    const below = rect.bottom + GAP_PX;
    const flip = below + menuHeight > window.innerHeight && rect.top - menuHeight - GAP_PX > 0;
    setPosition({
      top: flip ? rect.top - menuHeight - GAP_PX : below,
      left: Math.max(GAP_PX, Math.min(rect.right - width, window.innerWidth - width - GAP_PX)),
    });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;
    // Dismiss on the click, not the mousedown, and let it reach its target —
    // otherwise the first click on any other control is spent closing this menu.
    const onOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      // The one exception: Modal treats a click on its overlay as "close the
      // dialog". Here that click is the menu's dismissal, nothing more.
      if (target instanceof Element && target.classList.contains(MODAL_OVERLAY_CLASS)) {
        e.stopPropagation();
      }
      close(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close(true);
    };
    const onReflow = () => close(false);
    document.addEventListener('click', onOutsideClick, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('click', onOutsideClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  if (items.length === 0) return null;

  // Drop refs left behind by a longer previous item list.
  itemRefs.current.length = items.length;

  const openAt = (index: number) => {
    setOpen(true);
    setActiveIndex(index);
  };

  const step = (delta: number) => {
    if (enabledIndexes.length === 0) return;
    const at = enabledIndexes.indexOf(activeIndex);
    const next = at === -1
      ? (delta > 0 ? 0 : enabledIndexes.length - 1)
      : (at + delta + enabledIndexes.length) % enabledIndexes.length;
    setActiveIndex(enabledIndexes[next]);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openAt(enabledIndexes[0] ?? -1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openAt(enabledIndexes[enabledIndexes.length - 1] ?? -1);
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      step(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      step(-1);
    } else if (e.key === 'Tab') {
      // The portal sits outside Modal's focus trap; hand focus back before the
      // default Tab moves on, or it lands behind the overlay.
      close(true);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="overflow-menu__trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        onClick={() => (open ? close(false) : openAt(-1))}
        onKeyDown={onTriggerKeyDown}
      >
        <span aria-hidden="true">&#8942;</span>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className="overflow-menu__list"
          style={{
            width: `${MENU_WIDTH_REM}rem`,
            top: `${position?.top ?? 0}px`,
            left: `${position?.left ?? 0}px`,
            visibility: position ? 'visible' : 'hidden',
          }}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item, index) => (
            <button
              key={item.key}
              ref={(el) => { itemRefs.current[index] = el; }}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={`overflow-menu__item${item.variant === 'danger' ? ' overflow-menu__item--danger' : ''}`}
              onClick={() => {
                close(true);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default OverflowMenu;
